import CardLayout from '@/components/common/CardLayout';
import Modal from '@/components/common/modals/Modal';
import { Paragraph } from '@/components/common/Typography';
import ModalManager, { useModal } from '@/components/ModalManager';
import { ButtonProps } from '@chakra-ui/react';
import { TrackingEventName } from '@common/react/tracking';
import { isString } from '@common/util/types';
import { useState } from 'react';

interface ConfirmModalProps {
    title: string;
    description: string | React.ReactNode;
    buttonText: string;
    onConfirm: () => Promise<void>;
    confirmButtonProps?: ButtonProps;
    // If none passed, secondary button will be "Cancel" & simply close the modal
    secondaryButtonText?: string;
    secondaryButtonProps?: ButtonProps;
    onSecondaryButton?: () => void | Promise<void>;
    onClickPrimaryEvent: TrackingEventName;
    onClickSecondaryEvent: TrackingEventName;
}

export const showConfirmModal = (props: ConfirmModalProps) =>
    ModalManager.show(ConfirmModal, props);

const ConfirmModal = ModalManager.create<ConfirmModalProps>(
    ({
        title,
        description,
        onConfirm,
        onSecondaryButton,
        buttonText,
        secondaryButtonText,
        confirmButtonProps,
        secondaryButtonProps,
        onClickPrimaryEvent,
        onClickSecondaryEvent,
    }) => {
        const modal = useModal();

        return (
            <Modal isOpen={modal.visible} onClose={modal.remove}>
                <InnerConfirmModal
                    title={title}
                    description={description}
                    buttonText={buttonText}
                    onConfirm={onConfirm}
                    onSecondaryButton={onSecondaryButton}
                    secondaryButtonText={secondaryButtonText}
                    confirmButtonProps={confirmButtonProps}
                    secondaryButtonProps={secondaryButtonProps}
                    onClickPrimaryEvent={onClickPrimaryEvent}
                    onClickSecondaryEvent={onClickSecondaryEvent}
                />
            </Modal>
        );
    }
);

export const InnerConfirmModal = ({
    title,
    description,
    buttonText,
    onConfirm,
    onSecondaryButton,
    secondaryButtonText,
    confirmButtonProps,
    secondaryButtonProps,
    onClickPrimaryEvent,
    onClickSecondaryEvent,
}: ConfirmModalProps) => {
    const modal = useModal();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSecondarySubmitting, setIsSecondarySubmitting] = useState(false);

    return (
        <CardLayout
            title={title}
            primaryButtonText={buttonText}
            primaryButtonProps={confirmButtonProps}
            isPrimaryButtonLoading={isSubmitting}
            onPrimaryButton={async () => {
                setIsSubmitting(true);

                try {
                    await onConfirm();
                    modal.remove();
                } finally {
                    setIsSubmitting(false);
                }
            }}
            onClickPrimaryEvent={onClickPrimaryEvent}
            onClickSecondaryEvent={onClickSecondaryEvent}
            secondaryButtonText={secondaryButtonText}
            isSecondaryButtonLoading={isSecondarySubmitting}
            secondaryButtonProps={secondaryButtonProps}
            onSecondaryButton={async () => {
                // If no secondary button action, just close the modal
                if (!onSecondaryButton) {
                    modal.remove();
                    return;
                }

                // Otherwise, run the secondary button action & close the modal
                // if it succeeds
                setIsSecondarySubmitting(true);
                try {
                    await onSecondaryButton();
                    modal.remove();
                } finally {
                    setIsSecondarySubmitting(false);
                }
            }}
        >
            {isString(description) ? (
                <Paragraph color="textMuted">{description}</Paragraph>
            ) : (
                description
            )}
        </CardLayout>
    );
};
